import React, { useState } from "react";
import { useHistory } from "react-router-dom";

import {
  Grid,
  Typography,
  Card,
  Radio,
  FormControlLabel,
  Box,
  CircularProgress,
  Divider,
} from "@material-ui/core";
import ArrowBackIcon from "@material-ui/icons/ArrowBack";
import NavigateNextIcon from "@material-ui/icons/NavigateNext";
import SaveIcon from "@material-ui/icons/Save";
import Button from "components/CustomButtons/Button";
import { quCreateColor } from "assets/jss/material-dashboard-pro-react";
import WritingTable from "../Writing/WritingTable";

import {
  deleteLabResourceAPI,
  addResourceToLabAPI,
} from "views/APIs/APIUtility";

// Contexts
import NotificationContext from "views/Components/Context.js";

import MDEditor from "@uiw/react-md-editor";
import FileUploadDialog from "../FileUploadDialog";
import MarkdownEditorWithPreview from "../../../components/MarkdownEditor/MarkdownEditorWithPreview";

const DataRequirements = ({
  description,
  handleNavigation,
  handleSetFileResources,
  labID,
  newResource,
  setNewResource,
  replace,
  setReplace,
  files,
  setFiles,
  instructions,
  setInstructions,
  handleSave,
}) => {
  const history = useHistory(); // Use useHistory() instead of useNavigate()
  const { showNotification } = React.useContext(NotificationContext);
  const [details, setDetails] = useState("");
  const [loading, setLoading] = useState({ isLoading: false, message: "" });

  const [newFiles, setNewFiles] = useState([]);
  const [selectedResources, setSelectedResources] = useState([]);
  const [resourceAddModal, setResourceAddModal] = useState(false);

  const handleDatasetTypeChange = (event) => {
    setInstructions({ ...instructions, datasetType: event.target.value });
  };

  const handleDetailsChange = (event) => {
    setDetails(event.target.value);
  };

  const handleNext = () => {
    handleSave(true, "Saving Data Requirements...");
    handleNavigation("Visualization Requirements");
  };

  const handleBack = () => {
    handleNavigation("General Requirements");
  };

  const handleDeleteResource = async (resource, index) => {
    try {
      const formData = new FormData();
      formData.append("lab_id", labID);
      formData.append("resource_id", resource.id);
      await deleteLabResourceAPI(formData);
      const updatedResources = files.filter((item, idx) => idx !== index);
      setFiles(updatedResources);
      showNotification("Resource deleted successfully", "success");
    } catch (error) {
      showNotification(error, "danger");
    } finally {
    }
  };

  const handleReplace = async (resourceID, file, file_type) => {};

  const handleDataSetDetailsChange = (event) => {
    setInstructions({ ...instructions, datasetDetails: event });
  };

  const handleAddResource = async () => {
    if (!newResource.name || !newResource.description || !newResource.file) {
      showNotification("Please fill all the fields", "danger");
      return;
    }
    try {
      setResourceAddModal(false);
      setLoading({ isLoading: true, message: "Adding File..." });

      if (!newResource) {
        throw new Error("No new resource provided.");
      }

      // Initialize FormData for the API request
      const formData = new FormData();

      // Add required fields to FormData
      formData.append("lab_id", labID);
      formData.append("resource_type", "Dataset");
      formData.append("resource_name", newResource.name);
      formData.append("resource_description", newResource.description);
      // Append the new file if it exists
      if (newResource.file) {
        formData.append("resource_file", newResource.file);
      }

      const response = await addResourceToLabAPI(formData);

      handleSetFileResources(response.raw_resources);
      showNotification("File Added successfully", "success");
      setNewResource({ name: "", description: "", file: null });
    } catch (error) {
      console.error("Error in replacing resource:", error.message || error);
      showNotification(
        `Failed to replace resource: ${error.message || "Unknown error"}`,
        "danger"
      );
    } finally {
      handleFileUploadDialogClose();
      setLoading({ isLoading: false, message: "" });
    }
  };

  const handleResourceAdditionModal = () => {
    setResourceAddModal(true);
  };

  const handleFileUploadDialogClose = () => {
    setResourceAddModal(false);
    setNewResource({ name: "", description: "", file: null });
  };

  const handleReplaceResource = async () => {};

  return (
    <div>
      {/* Pass props to FileUploadDialog */}
      <FileUploadDialog
        open={resourceAddModal}
        onClose={handleFileUploadDialogClose}
        newFile={newResource}
        setNewFile={setNewResource}
        onSave={replace ? handleReplaceResource : handleAddResource}
        replace={replace}
        file_type="dataset"
      />
      {loading.isLoading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="60vh"
        >
          <CircularProgress />
          <Typography
            variant="h6"
            style={{ marginLeft: 16, textTransform: "none" }}
          >
            {loading.message}
          </Typography>
        </Box>
      ) : (
        <>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="body1" style={{ marginBottom: "10px" }}>
                {description}
              </Typography>
              <Divider style={{ marginBottom: "10px" }} />
            </Grid>
            <Grid item xs={12} sm={12}>
              <div>
                <Typography variant="h5" style={{ marginBottom: "10px" }}>
                  Dataset Type
                </Typography>
              </div>
              {/* First Radio Button: Synthetic */}
              <FormControlLabel
                control={<Radio />}
                label="Synthetic"
                value="Synthetic"
                checked={instructions.datasetType === "Synthetic"} // Ensures only one can be selected
                onChange={handleDatasetTypeChange}
                labelPlacement="end"
                sx={{ color: quCreateColor }}
              />
              {/* Second Radio Button: Real */}
              <FormControlLabel
                control={<Radio />}
                label="Real"
                value="Real"
                checked={instructions.datasetType === "Real"} // Ensures only one can be selected
                onChange={handleDatasetTypeChange}
                labelPlacement="end"
              />
              {/* Third Radio Button: No dataset */}
              <FormControlLabel
                control={<Radio />}
                label="No dataset"
                value="None"
                checked={instructions.datasetType === "None"} // Ensures only one can be selected
                onChange={handleDatasetTypeChange}
                labelPlacement="end"
              />
            </Grid>
            <Grid item xs={12}>
              {instructions.datasetType !== "No dataset" && (
                <Grid item xs={12} sm={12}>
                  <Card>
                    <div data-color-mode="light">
                      <MarkdownEditorWithPreview
                        value={instructions.datasetDetails || ""}
                        onChange={(event) => handleDataSetDetailsChange(event)}
                        height={"300px"}
                      />
                    </div>
                  </Card>
                </Grid>
              )}
            </Grid>
          </Grid>

          {/* 
                    <Grid container spacing={2} style={{ marginTop: "10px" }}>
                        <Grid item xs={12}>
                            <Typography variant="h5" style={{ marginBottom: "16px" }}>Manage Datasets</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <WritingTable
                                files={files}
                                handleDeleteResource={handleDeleteResource}
                                handleReplace={handleReplace}
                                handleResourceAddition={handleResourceAdditionModal}
                                setSelectedResources={setNewFiles}
                                selectedResources={selectedResources}
                                selectColumnRequired={false}
                                viewActionRequired={false}
                                atleastOneFileRequired={false}
                            />
                        </Grid>
                    </Grid> */}

          <Grid container style={{ marginTop: "10px" }}>
            <Grid item xs={12}>
              <Button
                color="quCreateColor"
                style={{ float: "right", fontWeight: "bold" }}
                endIcon={<NavigateNextIcon />}
                onClick={handleNext}
              >
                Next
              </Button>
              <Button
                color="quCreateColor"
                style={{ float: "right", fontWeight: "bold" }}
                startIcon={<SaveIcon />}
                onClick={handleSave}
              >
                Save
              </Button>
              <Button
                color="quCreateColor"
                style={{ float: "left", fontWeight: "bold" }}
                startIcon={<ArrowBackIcon />}
                onClick={handleBack}
              >
                Back
              </Button>
            </Grid>
          </Grid>
        </>
      )}
    </div>
  );
};

export default DataRequirements;
